import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { compose } from '../../services/utilityFunctions';
import translate from '../translate';
import Carousel from '../carousel/Carousel';
import ServerSideCarousel from '../carousel/ServerSideCarousel';
import Loader from '../../services/loader';
import withCSRF from '../withCSRF';
import Notifications from '../../services/notifications';
import BasicPortlet from '../portlets/BasicPortlet';
import CertificateItem from './CertificateItem';

const CertificateZipList = props => {
  const { t, _csrf, filters } = props;
  const { type, startDate, endDate, team, user, course, journey, session, typeCourses, typeSessions } = filters || {};
  const apiCarouselHeaders = {
    'Accept-Version': '1.0.0',
    'X-Gamification-API': 'Games',
    'Content-Type': 'application/json',
  };

  const [certificatesListSize, setCertificatesListSize] = useState(0);
  const [certificatesLoaded, setCertificatesLoaded] = useState(false);

  /** BEGIN:LOADER */
  const loader = useRef(new Loader('#report-certificatesList'));
  const [isLoading, setLoading] = useState(false);

  /** END:LOADER */

  const composeEndpoint = url => {
    let startQuery = '';
    let endQuery = '';
    let teamQuery = '';
    let ownerQuery = '';
    let courseQuery = '';
    let sessionQuery = '';
    let journeyQuery = '';
    let typeCoursesQuery = '';
    let typeSessionsQuery = '';

    if (startDate && endDate) {
      startQuery = `&dateStart=${startDate}`;
      endQuery = `&dateEnd=${endDate}`;
    }
    if (team) teamQuery = `&team=${team}`;
    if (user) ownerQuery = `&owner=${user}&type=${type}`;
    if (course) courseQuery = `&course=${course}`;
    if (session) sessionQuery = `&session=${session}`;
    if (journey) journeyQuery = `&journey=${journey}`;
    if (!course && typeCourses) typeCoursesQuery = `&typeCourses=${typeCourses}`;
    if (!session && typeSessions) typeSessionsQuery = `&typeSessions=${typeSessions}`;

    return `${url}populate=owner${startQuery}
    ${endQuery}${teamQuery}${ownerQuery}${journeyQuery}${courseQuery}${sessionQuery}${typeCoursesQuery}${typeSessionsQuery}`;
  };

  /** BEGIN: API JOBS */
  const checkDownloadStatus = jobId => {
    axios.get(`/jobs/academy/certificate/status/${jobId}`)
      .then(({ data: response }) => {
        const { progress, finishedOn, failedReason } = response.data;
        loader.current.showProgressLoader(`${progress.toFixed(0)}% Processing...`);
        if (finishedOn) {
          loader.current.hide();
          setLoading(false);
          if (failedReason) {
            Notifications.showNotificationError(t('error'), failedReason);
          } else {
            Notifications.showNotificationSuccess(t('success'), t('SUCCESS_EXPORT_CERTIFICATES'));
            window.open(response.data.returnvalue, '_blank');
          }
        } else {
          // Repeat until import of products is finished
          setTimeout(() => checkDownloadStatus(jobId), 1000);
        }
        return response.data;
      })
      .catch(err => {
        loader.current.hide();
        setLoading(false);
        Notifications.showErrorFromRequest(err, 'report.certificates.error.zip');
      });
  };

  const getZippedCertificates = () => {
    axios.get(composeEndpoint('/jobs/academy/certificate/generate/pdf/zip?'), {
      _csrf,
    })
      .then(({ data: response }) => {
        setLoading(true);
        loader.current.show({
          message: `${t('order_status_processing')}... 0%`,
        });
        setTimeout(() => {
          checkDownloadStatus(response.data.id);
        }, 1000);
      })
      .catch(err => {
        console.log(err);
      });
  };
  /** END: API JOBS */

  const handleRedirection = certificateId => {
    window.open(`/certificate/${certificateId}`);
  };

  const renderCertificates = (certificates = []) => {
    setCertificatesListSize(certificates.length);
    setCertificatesLoaded(true);
    return (
      certificates.length > 0 ?
        certificates.map(certificate => (
          <button type="button" className="btn-no-style" onClick={() => handleRedirection(certificate._id)}>
            <CertificateItem key={certificate._id} certificate={certificate} />
          </button>
        ))
        :
        <div className="no-certificates-found"><span className="text">{t('report.certificates.notFound')}</span></div>
    );
  };

  if (certificatesLoaded && certificatesListSize === 0) return null;

  return (
    <div className="certificate-report-list">
      <BasicPortlet
        title={t('certificates')}
        titleClass="m--font-primary upperCase-title"
        toolClass="btn btn-brand btn-sm"
        toolAnchor={getZippedCertificates}
        subtitle={`${t('report.certificates.subtitle')}`}
        isButton
        disabled={isLoading}
        toolTitle={t('report.certificates.downloadAll')}
        showBtn={certificatesListSize > 0}
      >
        <Carousel>
          <ServerSideCarousel
            id="report-certificatesList"
            fetchUrl={composeEndpoint('/table/certificate/list?')}
            defaultFetchError="ERROR_FETCH_CERTIFICATES"
            fetchHeaders={apiCarouselHeaders}
          >
            {certificates => renderCertificates(certificates)}
          </ServerSideCarousel>
        </Carousel>
      </BasicPortlet>
    </div>
  );
};

CertificateZipList.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  filters: PropTypes.shape({
    type: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    team: PropTypes.string,
    user: PropTypes.string,
    course: PropTypes.string,
    session: PropTypes.string,
    journey: PropTypes.string,
    typeCourses: PropTypes.string,
    typeSessions: PropTypes.string,
  }),
};

CertificateZipList.defaultProps = {
  filters: {},
};

export default compose(
  translate,
  withCSRF,
)(CertificateZipList);
